import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import ChatHeader from "../../components/chat-header";
import { useLayoutContext } from "../../layouts/general-layout";
import { examImg, Loader } from "../../assets/images/index";
import { RouterConstant } from "../../routers/index";
import Button from "../../components/button";
import { SelfTestService } from "../../services/self-test/self-test-service";
import { useSelfTestStore } from "../../utils/selfTestStore";
import "./self-test-start.scss";

interface Option {
    id: string;
    name: string;
  }

export default function Index() {
    const { t } = useLayoutContext();
    const navigate = useNavigate();
    const { ROUTERS_PATH } = RouterConstant;
    const [levelOptions, setLevelOptions] = useState<Option[]>([]);
    const [topicOptions, setTopicOptions] = useState<Option[]>([]);
    const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [isLevelLoading, setIsLevelLoading] = useState<boolean>(false);
    const [isTopicLoading, setIsTopicLoading] = useState<boolean>(false);
    const selfTestService = new SelfTestService();
    const clearAll = useSelfTestStore((s) => s.clearAll);

    useEffect(() => {
        fetchLevels();
        clearAll();
    }, []);


    const fetchLevels = async () => {
        setIsLevelLoading(true);
        try {
            const response = await selfTestService.getSelfTestLevels();
            const levels = response?.levels || []
            const filteredLevels = levels.filter((lvl: any) => lvl.code !== 3);
            setLevelOptions(filteredLevels);
            let defaultLevel = filteredLevels.find((lvl: any) => lvl.code === 5);
            if (!defaultLevel && filteredLevels.length > 0) {
                defaultLevel = filteredLevels[0];
            }
    
            if (defaultLevel) {
                fetchTopics(defaultLevel.id);
            }
        } catch (err) {
            console.error("Failed to fetch levels", err);
        } finally {
            setIsLevelLoading(false);
        }
    };
    
    const fetchTopics = async (levelId: string) => {
        setSelectedLevel(levelId);
        setTopicOptions([]);
        setSelectedTopic(null);
        setIsTopicLoading(true);
    
        try {
            const response = await selfTestService.getSelfTestTopics(levelId);
            const topics = response?.topics || [];
            setTopicOptions(topics);
        } catch (err) {
            console.error("Failed to fetch topics for level", err);
        } finally {
            setIsTopicLoading(false);
        }
    };
    
    const handleStartClick = () => {
        navigate(ROUTERS_PATH.SELFTESTEXERCISES, {
            state: {
                selectedLevelId: selectedLevel,
                selectedTopicId: selectedTopic,
            },
        });
    };

    return (
        <div className="self-test-start-page">
            <ChatHeader
                chatHeading={t("testYourself")}
                onBackClick={() => navigate(ROUTERS_PATH.USER_MAIN_DASHBOARD)}
                hideLogoutLogo={true}
            />
            <div className="self-test-start-content">
                <div className="self-test-start-list">
                    <div className="heading">
                        <h3>{t("chooseTopicForExam")}</h3>
                        <p>{t("examInstructions")}</p>
                        <img src={examImg} alt="Exam" />
                    </div>
                    <div className="chips-wrapper">
                        <p>{t("level")}{isLevelLoading && (
                            <img src={Loader} alt="Loading..." className="inline-loader" />
                        )}</p>
                        
                        <div className="chips-list">
                            {levelOptions.map((level) => (
                                <button
                                    key={level.id}
                                    className={`chip ${selectedLevel === level.id ? "selected" : ""}`}
                                    onClick={() => fetchTopics(level.id)}
                                >
                                    {level.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="chips-wrapper">
                        <p>{t("carrier")}{isTopicLoading && (
                            <img src={Loader} alt="Loading..." className="inline-loader" />
                        )}</p>
                        
                        <div className="chips-list topic-list">
                            {topicOptions.map((topic) => (
                                <button
                                    key={topic.id}
                                    className={`chip ${selectedTopic === topic.id ? "selected" : ""}`}
                                    onClick={() => setSelectedTopic(topic.id)}
                                >
                                    {topic.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="self-test-start-button">
                    <Button
                        type="button"
                        text={t("start")}
                        color="blue"
                        onClick={handleStartClick}
                        disabled={!selectedLevel || !selectedTopic}
                    />
                </div>
            </div>
        </div>
    );
}
